import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Building2, Save, Loader2, CreditCard, Phone, MapPin, User } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FacilitySettingsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [facilityId, setFacilityId] = useState('');
  const [formData, setFormData] = useState({
    name: '', address: '', phone: '', municipality: '津市',
    representative: '', company_name: '', postal_code: '',
    bank_name: '', branch_name: '', account_type: '普通', account_number: '', account_holder: '',
  });

  useEffect(() => { fetchFacility(); }, []);

  const fetchFacility = async () => {
    try {
      const res = await axios.get(`${API}/facilities`);
      if (res.data.length > 0) {
        const f = res.data[0];
        setFacilityId(f.id);
        setFormData({
          name: f.name || '',
          address: f.address || '',
          phone: f.phone || '',
          municipality: f.municipality || '津市',
          representative: f.representative || '',
          company_name: f.company_name || '',
          postal_code: f.postal_code || '',
          bank_name: f.bank_name || '',
          branch_name: f.branch_name || '',
          account_type: f.account_type || '普通',
          account_number: f.account_number || '',
          account_holder: f.account_holder || '',
        });
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!facilityId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/facilities/${facilityId}`, formData);
      alert('事業所情報を保存しました');
    } catch (e) {
      alert('保存に失敗しました: ' + (e.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl" data-testid="facility-settings-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">事業所設定</h1>
        <p className="text-muted-foreground mt-1">事業所情報・振込先の設定（請求書に反映）</p>
      </div>

      {/* 基本情報 */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Building2 size={20} className="text-primary" />基本情報</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>法人名</Label>
              <Input value={formData.company_name} onChange={e => handleChange('company_name', e.target.value)} placeholder="株式会社プレサージュ" data-testid="company-name" />
            </div>
            <div className="space-y-1">
              <Label>事業所名 *</Label>
              <Input value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="日中一時支援みのり" data-testid="facility-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>代表者</Label>
              <Input value={formData.representative} onChange={e => handleChange('representative', e.target.value)} placeholder="代表取締役 鳥飼 巧" data-testid="representative" />
            </div>
            <div className="space-y-1">
              <Label>自治体</Label>
              <Input value={formData.municipality} onChange={e => handleChange('municipality', e.target.value)} data-testid="municipality" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-1">
              <Label>郵便番号</Label>
              <Input value={formData.postal_code} onChange={e => handleChange('postal_code', e.target.value)} placeholder="514-0011" data-testid="postal-code" />
            </div>
            <div className="md:col-span-2 space-y-1">
              <Label>住所 *</Label>
              <Input value={formData.address} onChange={e => handleChange('address', e.target.value)} placeholder="三重県津市高洲町23番25号" data-testid="address" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>電話番号 *</Label>
            <Input value={formData.phone} onChange={e => handleChange('phone', e.target.value)} placeholder="050-6863-7610" data-testid="phone" />
          </div>
        </CardContent>
      </Card>

      {/* 振込先 */}
      <Card className="card-surface">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CreditCard size={20} className="text-primary" />振込先口座（請求書に表示）</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>銀行名</Label>
              <Input value={formData.bank_name} onChange={e => handleChange('bank_name', e.target.value)} placeholder="PayPay銀行" data-testid="bank-name" />
            </div>
            <div className="space-y-1">
              <Label>支店名</Label>
              <Input value={formData.branch_name} onChange={e => handleChange('branch_name', e.target.value)} placeholder="ビジネス営業部" data-testid="branch-name" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label>口座種類</Label>
              <Input value={formData.account_type} onChange={e => handleChange('account_type', e.target.value)} placeholder="普通" data-testid="account-type" />
            </div>
            <div className="space-y-1">
              <Label>口座番号</Label>
              <Input value={formData.account_number} onChange={e => handleChange('account_number', e.target.value)} placeholder="1335215" data-testid="account-number" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>口座名義</Label>
            <Input value={formData.account_holder} onChange={e => handleChange('account_holder', e.target.value)} placeholder="カ）プレサージュ" data-testid="account-holder" />
          </div>
        </CardContent>
      </Card>

      <Button className="w-full btn-active" onClick={handleSave} disabled={saving} data-testid="save-facility-button">
        {saving ? <><Loader2 size={18} className="mr-2 animate-spin" />保存中...</> : <><Save size={18} className="mr-2" />事業所情報を保存</>}
      </Button>
    </div>
  );
}
